import React from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { gbp } from "../../lib/apiClient";
import { Section } from "./layout";
import { useRowDrag } from "./useRowDrag";
import { numVal, parseNum, tmpId, DIFFICULTIES } from "./utils";
import { HoursCell } from "./HoursCell";

// Work types whose Product Type + Difficulty columns are user-editable
// (vs. shown as an em-dash placeholder). Mirrors the backend constant
// `PRODUCT_DRIVEN_WORK_TYPES` in `routes/quotes.py`. Keep these in sync.
const PRODUCT_DRIVEN_WORK_TYPES = new Set(["Fresh Install", "Rip Out", "Repair"]);
const isProductDriven = (wt) => PRODUCT_DRIVEN_WORK_TYPES.has(wt);
// Repair is product-driven for backend purposes (difficulty multiplier still
// applies) but does NOT show the Product Type cell — there's no catalogue
// preset for Repair so a product reference adds nothing for the estimator.
const showsProductType = (wt) => isProductDriven(wt) && wt !== "Repair";

/**
 * LabourTable — labour-item rows on a quote.
 * Hides the Product Type column for Repair jobs. Travel rows added via
 * postcode auto-fill carry `_auto_travel: true` and render a read-only branch chip.
 */
export function LabourTable({
  items,
  onChange,
  branches = [],
  labourTypes = [],
  doorProducts = [],
  quoteBranch = "",
  jobType = "Install",
  storageKey,
  costSettings = null,
  canAddHours = true,
  canSubtractHours = true,
  canOverrideHoursFloor = false,   // true for admin or manager
  userInitials = "",
}) {
  const drag = useRowDrag(items || [], onChange, "labour");

  const updateLabour = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeLabour = (idx) => onChange(items.filter((_, i) => i !== idx));

  // How many Complex hours does this row get? Product override wins; falls
  // back to the global `complex_default_hours` from cost settings (default 4).
  const complexHoursFor = (productName) => {
    const p = doorProducts.find((x) => x.name === productName);
    const perProduct = p?.complex_hours;
    if (perProduct != null && perProduct !== "") return Number(perProduct) || 0;
    return Number(costSettings?.complex_default_hours ?? 4) || 0;
  };

  const onLabourProduct = (idx, productName) => {
    const p = doorProducts.find((x) => x.name === productName);
    if (!p) return updateLabour(idx, { product_name: productName });
    const cur = items[idx];
    const wt = cur.work_type;
    // Repair has no preset hours; leave hours untouched and only update the
    // product label. Fresh Install + Rip Out auto-fill from catalogue.
    if (wt === "Repair") {
      return updateLabour(idx, { product_name: productName });
    }
    const isFresh = wt === "Fresh Install";
    const autoHrs = isFresh ? p.install_hours_fresh : p.install_hours_ripout;
    // If the row was already Complex, the visible `hours` must include the
    // Complex bonus for the NEW product (so swapping High-Speed → Sectional
    // re-folds the right amount). _complex_folded_in stays true.
    const isComplex = cur.difficulty === "Complex";
    const complexAdd = isComplex ? (Number(p.complex_hours ?? costSettings?.complex_default_hours ?? 4) || 0) : 0;
    updateLabour(idx, {
      product_name: productName,
      hours: Number(autoHrs || 0) + complexAdd,
      _auto_hours: autoHrs,
      _complex_folded_in: isComplex,
      contingency_hours: isFresh ? p.contingency_fresh : p.contingency_ripout,
    });
  };

  const onLabourWorkType = (idx, wt) => {
    const cur = items[idx];
    // Switching to a work-type where Product/Difficulty are hidden — wipe the
    // floor + difficulty so a stale value can't silently inflate (Complex) or
    // lock (auto-hours floor) the row going forward.
    if (!isProductDriven(wt)) {
      return updateLabour(idx, {
        work_type: wt,
        _auto_hours: null,
        difficulty: "Standard",
        _complex_folded_in: false,
        contingency_hours: 0,
        // Clear any prior hours-override audit; the new work-type has no floor.
        hours_override_by: "",
        hours_override_at: "",
      });
    }
    const p = doorProducts.find((x) => x.name === cur.product_name);
    if (!p) return updateLabour(idx, { work_type: wt });
    // Fresh Install + Rip Out have preset hours per product in Settings →
    // Catalogue. Repair has a flat 4.5h minimum (admin-tunable via
    // `cost_settings.repair_minimum_hours`) — the floor is propagated via
    // `_auto_hours` so HoursCell + the backend `_enforce_labour_hours_floor`
    // use the same value.
    if (wt === "Repair") {
      // Per-key-account override: if the row's branch (or the quote-level
      // branch) has `min_repair_hours` set (even 0), it wins over the global
      // default from cost_settings. Enables clients like Lorne Stewart - CCC
      // to book travel-only Repair jobs.
      const rowBranchName = cur.branch || quoteBranch;
      const rowBranch = branches.find((b) => b.name === rowBranchName);
      const globalMin = Number(costSettings?.repair_minimum_hours ?? 4.5);
      const repairMin = rowBranch && rowBranch.min_repair_hours != null
        ? Number(rowBranch.min_repair_hours)
        : globalMin;
      const curHours = Number(cur.hours || 0);
      return updateLabour(idx, {
        work_type: wt,
        product_name: "",
        custom_product: "",
        _auto_hours: repairMin,
        _complex_folded_in: false,
        difficulty: "Standard",
        contingency_hours: 0,
        // Bump hours up to the floor if currently below (estimators bypassed
        // would just be snapped back by HoursCell anyway). Preserve overrides.
        hours: curHours < repairMin ? repairMin : curHours,
        hours_override_by: "",
        hours_override_at: "",
      });
    }
    const autoHrs = wt === "Fresh Install" ? p.install_hours_fresh : p.install_hours_ripout;
    // Preserve Complex if it was already set — recompute the folded-in total
    // for the new work-type's baseline.
    const isComplex = cur.difficulty === "Complex";
    const complexAdd = isComplex ? (Number(p.complex_hours ?? costSettings?.complex_default_hours ?? 4) || 0) : 0;
    updateLabour(idx, {
      work_type: wt,
      hours: Number(autoHrs || 0) + complexAdd,
      _auto_hours: autoHrs,
      _complex_folded_in: isComplex,
      contingency_hours: wt === "Fresh Install" ? p.contingency_fresh : p.contingency_ripout,
      // Resetting hours to the new auto-fill — clear any prior override audit.
      hours_override_by: "",
      hours_override_at: "",
    });
  };

  // Toggling Difficulty Standard ↔ Complex must visibly bump the Hours box
  // up or down by complex_hours so the estimator sees what's added. The
  // `_complex_folded_in` flag tells the backend not to re-add complex_hours
  // (otherwise we'd double-count).
  const onLabourDifficulty = (idx, newDiff) => {
    const cur = items[idx];
    const wt = (cur.work_type || "").trim();
    if (!isProductDriven(wt) || cur._auto_travel) {
      // Difficulty dropdown is hidden for these — defensively only update label.
      return updateLabour(idx, { difficulty: newDiff });
    }
    const complexN = complexHoursFor(cur.product_name);
    const prevDiff = cur.difficulty || "Standard";
    const wasFolded = !!cur._complex_folded_in;
    let nextHours = Number(cur.hours || 0);
    let nextFolded = wasFolded;
    if (newDiff === "Complex" && prevDiff !== "Complex") {
      // Standard → Complex: add complex hours, mark folded.
      nextHours += complexN;
      nextFolded = true;
    } else if (newDiff !== "Complex" && prevDiff === "Complex") {
      // Complex → Standard (or anything else): subtract complex hours IF they
      // were folded in. Floor at the auto-hours (or 0) so we don't go negative
      // on weird edge cases.
      if (wasFolded) {
        nextHours = Math.max(0, nextHours - complexN);
      }
      nextFolded = false;
    }
    updateLabour(idx, {
      difficulty: newDiff,
      hours: nextHours,
      _complex_folded_in: nextFolded,
    });
  };

  const addLabour = () => onChange([
    ...(items || []),
    {
      id: tmpId(),
      branch: quoteBranch || "",
      product_name: "",
      custom_product: "",
      work_type: jobType === "Repair" ? "Repair" : "Fresh Install",
      hours: 0,
      multiplier: 1,
      difficulty: "Standard",
      contingency_hours: 0,
    },
  ]);

  return (
    <Section
      title="Labour"
      subtitle="Pick the Labour Type for the rate per line — add a Travel row for site travel hours"
      collapsible
      storageKey={storageKey}
      action={
        <button
          onClick={addLabour}
          className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 hover:text-slate-900 text-slate-600"
          data-testid="add-labour-button"
        >
          <Plus size={14} /> Add labour
        </button>
      }
    >
      <div>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th style={{ width: 28 }} className="px-1 py-2"></th>
              {[
                "Labour Type",
                "Product Type",
                "Work Type", "Hours", "Multiplier", "Difficulty", "Rate £/hr", "Subtotal", "",
              ].map((h) => (
                <th key={h} className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(items || []).length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400 text-sm">No labour lines yet.</td></tr>
            )}
            {(items || []).map((lb, idx) => {
              const lineBranch = lb.branch || quoteBranch;
              const rate = branches.find((b) => b.name === lineBranch)?.labour_rate || 0;
              // Complex difficulty: add complex_hours to the row's hours.
              // Only count it on work types where difficulty is actually
              // shown (Fresh Install / Repair). For Travel/Rip Out/Other the
              // dropdown is hidden, so a stale "Complex" value left behind
              // from an earlier work-type pick must NOT silently inflate the
              // hours total.
              const isAutoTravel = !!lb._auto_travel;
              const difficultyApplies = !isAutoTravel && isProductDriven(lb.work_type);
              // Complex bonus is now FOLDED INTO `hours` on the frontend (so
              // the estimator sees 19 instead of 15 when toggling Complex).
              // For legacy rows where `_complex_folded_in` is false, we still
              // need to surface the +N in the subtotal so the cost stays
              // accurate while the row is being touched.
              const legacyComplexAdd = (difficultyApplies && lb.difficulty === "Complex" && !lb._complex_folded_in)
                ? complexHoursFor(lb.product_name)
                : 0;
              const lineMult = Number(lb.multiplier ?? 1) || 1;
              const subtotal = (Number(lb.hours || 0) + legacyComplexAdd) * lineMult * rate;
              const isCustom = lb.product_name === "__CUSTOM__";
              return (
                <tr key={lb.id} className={`border-b border-slate-200 ${isAutoTravel ? "bg-orange-50/40" : ""}`} {...drag.dragProps(idx)}>
                  <td className="px-1 py-1 align-middle text-slate-400 hover:text-slate-700" {...drag.handleProps(idx)}>
                    <GripVertical size={14} />
                  </td>
                  <td className="px-1 py-1 w-44">
                    {isAutoTravel ? (
                      <div className="h-9 flex items-center text-sm">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-orange/10 text-brand-orange text-[10px] uppercase tracking-wider font-bold mr-1.5">Auto</span>
                        <span className="text-slate-700 text-[13px] truncate">{lineBranch || "—"}</span>
                      </div>
                    ) : (
                      <select
                        value={lb.branch || ""}
                        onChange={(e) => updateLabour(idx, { branch: e.target.value })}
                        className="qb-input h-9"
                        data-testid={`labour-branch-${idx}`}
                      >
                        <option value="">{quoteBranch ? `Use quote (${quoteBranch})` : "—"}</option>
                        {labourTypes.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                      </select>
                    )}
                  </td>
                  {(() => {
                    // Product Type cell — visible for Fresh Install + Rip Out.
                    // Hidden for Repair, Travel, Other and Auto-Travel rows.
                    // Repair-specific: catalogue has no Repair preset, so a
                    // product reference adds no value for the estimator.
                    const productRelevant = !isAutoTravel && showsProductType(lb.work_type);
                    return (
                      <td className="px-1 py-1">
                        {!productRelevant ? (
                          <div className="h-9 flex items-center px-2 text-[12px] text-slate-400 italic">—</div>
                        ) : (
                          <>
                            <select
                              value={lb.product_name}
                              onChange={(e) => onLabourProduct(idx, e.target.value)}
                              className="qb-input h-9"
                              data-testid={`labour-product-${idx}`}
                            >
                              <option value="">—</option>
                              {doorProducts.map((p) => <option key={p.id}>{p.name}</option>)}
                              <option value="__CUSTOM__">+ Custom / Nominal item…</option>
                            </select>
                            {isCustom && (
                              <input
                                value={lb.custom_product || ""}
                                onChange={(e) => updateLabour(idx, { custom_product: e.target.value })}
                                placeholder="Type a labour description"
                                className="qb-input h-8 mt-1 text-xs"
                                data-testid={`labour-custom-${idx}`}
                              />
                            )}
                          </>
                        )}
                      </td>
                    );
                  })()}
                  <td className="px-1 py-1 w-48">
                    {isAutoTravel ? (
                      <div className="h-9 flex items-center px-2 text-[13px] text-slate-700">Travel</div>
                    ) : (lb.product_name || "").toLowerCase().includes("barrel conversion") ? (
                      // Barrel Conversion is a self-contained product — work
                      // type doesn't drive its calc, so the cell shows a
                      // locked dash instead of the dropdown.
                      <div className="h-9 flex items-center px-2 text-[13px] text-slate-400" data-testid={`labour-worktype-${idx}-locked`}>—</div>
                    ) : (
                      <select
                        value={lb.work_type}
                        onChange={(e) => onLabourWorkType(idx, e.target.value)}
                        className="qb-input h-9"
                        data-testid={`labour-worktype-${idx}`}
                      >
                        {jobType !== "Repair" && <option>Fresh Install</option>}
                        {jobType !== "Repair" && <option>Rip Out</option>}
                        <option>Repair</option>
                        <option>Travel</option>
                        <option>Other</option>
                      </select>
                    )}
                  </td>
                  <td className="px-1 py-1 w-20">
                    {(() => {
                      // Floor source-of-truth: prefer the persisted `_auto_hours`
                      // (what was auto-filled when the user picked the product),
                      // but FALL BACK to looking up the current catalogue entry.
                      // Without this fallback, a saved quote that round-tripped
                      // through the backend and lost `_auto_hours` would render
                      // via the no-floor plain-input path — bypassing the role
                      // gate entirely (the bug Alex Welch reported).
                      let autoHrs = lb._auto_hours;
                      if (autoHrs == null && lb.product_name && doorProducts.length) {
                        const p = doorProducts.find((x) => x.name === lb.product_name);
                        if (p) {
                          autoHrs = lb.work_type === "Fresh Install"
                            ? p.install_hours_fresh
                            : p.install_hours_ripout;
                        }
                      }
                      // If Complex is folded into the visible Hours, the floor
                      // must include the bonus too. e.g. 15 install + 4 complex
                      // = 19 floor; an estimator without can_subtract_hours
                      // can't drop below 19. Mirrors the backend
                      // `_enforce_labour_hours_floor` Complex branch.
                      const isComplexFolded = lb.difficulty === "Complex" && !!lb._complex_folded_in && difficultyApplies;
                      const effectiveAutoHrs = (autoHrs != null && isComplexFolded)
                        ? Number(autoHrs || 0) + complexHoursFor(lb.product_name)
                        : autoHrs;
                      const hasAuto = effectiveAutoHrs != null && effectiveAutoHrs !== "" && !isAutoTravel;
                      if (isAutoTravel) {
                        return (
                          <input
                            type="number" step="0.25"
                            value={numVal(lb.hours)}
                            readOnly
                            className="qb-input h-9 font-mono bg-slate-50"
                            data-testid={`labour-hours-${idx}`}
                            title="Travel hours come from the postcode settings"
                          />
                        );
                      }
                      // No auto-fill yet (no product selected) — accept any
                      // value freely, no floor.
                      if (!hasAuto) {
                        return (
                          <input
                            type="number" step="0.25"
                            value={numVal(lb.hours)}
                            onChange={(e) => updateLabour(idx, { hours: parseNum(e.target.value) })}
                            className="qb-input h-9 font-mono"
                            data-testid={`labour-hours-${idx}`}
                          />
                        );
                      }
                      return (
                        <HoursCell
                          value={lb.hours}
                          autoHrs={effectiveAutoHrs}
                          userInitials={userInitials}
                          overrideBy={lb.hours_override_by}
                          overrideAt={lb.hours_override_at}
                          canOverrideFloor={canOverrideHoursFloor}
                          canAdd={canAddHours}
                          canSubtract={canSubtractHours}
                          onChangeWithAudit={(hrs, audit) => updateLabour(idx, { hours: hrs, ...audit })}
                          testId={`labour-hours-${idx}`}
                        />
                      );
                    })()}
                  </td>
                  <td className="px-1 py-1 w-16">
                    <select
                      value={String(lineMult)}
                      onChange={(e) => updateLabour(idx, { multiplier: parseFloat(e.target.value) || 1 })}
                      className="qb-input h-9 font-mono"
                      data-testid={`labour-multiplier-${idx}`}
                    >
                      <option value="1">1x</option>
                      <option value="1.5">1.5x</option>
                      <option value="2">2x</option>
                    </select>
                  </td>
                  <td className="px-1 py-1 w-40">
                    {(() => {
                      // Difficulty applies to any product-driven work
                      // type (Fresh Install / Rip Out / Repair). Travel /
                      // Other / Auto-Travel rows always stay at Standard so
                      // the multiplier is academic — hide the dropdown for
                      // visual cleanliness.
                      const difficultyRelevant = !isAutoTravel && isProductDriven(lb.work_type);
                      if (!difficultyRelevant) {
                        return <div className="h-9 flex items-center px-2 text-[12px] text-slate-400 italic">—</div>;
                      }
                      return (
                        <select
                          value={lb.difficulty}
                          onChange={(e) => onLabourDifficulty(idx, e.target.value)}
                          className="qb-input h-9"
                          data-testid={`labour-difficulty-${idx}`}
                        >
                          {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
                        </select>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-1 w-20 font-mono text-right whitespace-nowrap text-slate-600" data-testid={`labour-rate-${idx}`}>
                    {rate
                      ? (lineMult !== 1
                          ? <span title={`£${rate}/hr × ${lineMult}x`}>{gbp(rate * lineMult)}</span>
                          : gbp(rate))
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-2 py-1 w-24 font-mono text-right whitespace-nowrap font-semibold">{gbp(subtotal)}</td>
                  <td className="px-2 py-1 w-10 text-right">
                    {isAutoTravel ? (
                      <span
                        className="inline-flex items-center justify-center w-7 text-slate-300 cursor-not-allowed"
                        title="Auto-travel row — controlled by the selected postcode. Remove the postcode to drop this line."
                        data-testid={`remove-labour-${idx}-locked`}
                      >
                        <Trash2 size={14} />
                      </span>
                    ) : (
                      <button onClick={() => removeLabour(idx)} className="text-slate-400 hover:text-red-600" data-testid={`remove-labour-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </Section>
  );
}
